/**
 * Reporting Core SDK - Filter Utilities
 *
 * Headless utilities for managing filter state.
 */

import type {
  Filter,
  FilterOption,
  FilterValues,
  OptionsCacheEntry,
  OptionsCacheKey,
  ReportConfig
} from './types';
import { ReportingApiError } from './types';

/**
 * System parameter prefix that must not be submitted.
 */
const SYSTEM_PARAM_PREFIX = '__';

/**
 * Initialize filter values from report config.
 *
 * Extracts default values from filter definitions.
 *
 * @param config - Report configuration
 * @returns Initial filter values with defaults applied
 */
export function initializeFilterValues(config: ReportConfig): FilterValues {
  const values: FilterValues = {};

  for (const filter of config.filters) {
    if (filter.defaultValue !== undefined) {
      values[filter.id] = filter.defaultValue;
    }
  }

  return values;
}

/**
 * Get visible (user-facing) filters from config.
 *
 * Excludes hidden and system filters.
 */
export function getVisibleFilters(config: ReportConfig): Filter[] {
  return config.filters.filter((f) => !f.hidden && f.source !== 'system');
}

/**
 * Get required filters from config.
 */
export function getRequiredFilters(config: ReportConfig): Filter[] {
  return config.filters.filter((f) => f.required);
}

/**
 * Check if a filter is query-backed (needs dynamic options loading).
 */
export function isQueryBackedFilter(filter: Filter): boolean {
  return filter.optionsSource?.type === 'query';
}

/**
 * Validate filter values before submission.
 *
 * Checks:
 * - No system parameter keys (__*) are included
 * - Required filters have values
 *
 * @throws ReportingApiError if validation fails
 */
export function validateFilterValues(
  filters: Filter[],
  values: FilterValues
): void {
  // Check for system parameter keys
  const systemKeys = Object.keys(values).filter((key) =>
    key.startsWith(SYSTEM_PARAM_PREFIX)
  );
  if (systemKeys.length > 0) {
    throw new ReportingApiError(
      `Cannot submit filter keys starting with "${SYSTEM_PARAM_PREFIX}": ${systemKeys.join(
        ', '
      )}`,
      {
        errorCode: 'VALIDATION_ERROR',
        details: { invalidKeys: systemKeys }
      }
    );
  }

  // Check required filters
  const missingRequired: string[] = [];
  for (const filter of filters) {
    if (filter.required && !filter.hidden && filter.source !== 'system') {
      const value = values[filter.id];
      if (value === undefined || value === null || value === '') {
        missingRequired.push(filter.id);
      }
    }
  }

  if (missingRequired.length > 0) {
    throw new ReportingApiError(
      `Missing required filter values: ${missingRequired.join(', ')}`,
      {
        errorCode: 'VALIDATION_ERROR',
        details: { missingFilters: missingRequired }
      }
    );
  }
}

/**
 * Prepare filter values for API submission.
 *
 * Removes any filter keys that start with the system prefix.
 *
 * @throws ReportingApiError if system keys are present
 */
export function prepareFiltersForSubmission(
  values: FilterValues
): FilterValues {
  const result: FilterValues = {};

  for (const [key, value] of Object.entries(values)) {
    if (key.startsWith(SYSTEM_PARAM_PREFIX)) {
      throw new ReportingApiError(
        `Cannot submit filter key "${key}" - system parameters are not allowed`,
        {
          errorCode: 'VALIDATION_ERROR',
          details: { invalidKey: key }
        }
      );
    }
    result[key] = value;
  }

  return result;
}

// ============================================================================
// Options Cache
// ============================================================================

/**
 * Options cache for query-backed filters.
 *
 * Keys are generated from report ID, filter ID, and dependency values.
 */
export class FilterOptionsCache {
  private cache = new Map<OptionsCacheKey, OptionsCacheEntry>();
  private ttlMs: number;

  /**
   * Create a new options cache.
   *
   * @param ttlMs - Time-to-live in milliseconds (default: 5 minutes)
   */
  constructor(ttlMs: number = 5 * 60 * 1000) {
    this.ttlMs = ttlMs;
  }

  /**
   * Generate a cache key from request parameters.
   */
  static createKey(
    reportId: string,
    filterId: string,
    dependencyValues?: Record<string, unknown>
  ): OptionsCacheKey {
    const deps = dependencyValues
      ? JSON.stringify(
          Object.entries(dependencyValues)
            .filter(([, v]) => v !== undefined && v !== null)
            .sort(([a], [b]) => a.localeCompare(b))
        )
      : '';
    return `${reportId}:${filterId}:${deps}`;
  }

  /**
   * Get cached options if available and not expired.
   */
  get(key: OptionsCacheKey): FilterOption[] | undefined {
    const entry = this.cache.get(key);
    if (!entry) {
      return undefined;
    }

    if (Date.now() - entry.fetchedAt > this.ttlMs) {
      this.cache.delete(key);
      return undefined;
    }

    return entry.options;
  }

  /**
   * Store options in the cache.
   */
  set(key: OptionsCacheKey, options: FilterOption[]): void {
    this.cache.set(key, {
      options,
      fetchedAt: Date.now()
    });
  }

  /**
   * Clear the entire cache.
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear cache entries for a specific report.
   */
  clearForReport(reportId: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${reportId}:`)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get the number of cached entries.
   */
  get size(): number {
    return this.cache.size;
  }
}

/**
 * Shared global options cache.
 */
export const globalOptionsCache = new FilterOptionsCache();
